<template>
  <div :class="{ 'has-logo': showLogo }" class="sidebar-inner">
    <logo v-if="showLogo" :collapse="isCollapse" />
    <el-scrollbar :class="sideTheme" wrap-class="scrollbar-wrapper">
      <el-menu style="flex-grow: 1;" :default-active="activeMenu" :collapse="isCollapse"
        :background-color="sidebarMenuBg"
        :text-color="sideTheme === 'theme-dark' ? variables.menuColor : variables.menuLightColor" :unique-opened="true"
        :active-text-color="theme" :collapse-transition="false" mode="vertical" @select="handleMenuSelect">
        <sidebar-item class="menu-item" v-for="(route, index) in sidebarRouters" :key="route.path + index" :item="route"
          :base-path="route.path" />
      </el-menu>
    </el-scrollbar>
    <!-- 底部固定区：用户信息 / powered by 仅展开时显示；工具栏在收起时也保留 -->
    <div class="sidebar-footer" :class="{ 'is-collapsed': isCollapse }">
      <div class="user-profile" v-if="!isCollapse">
        <div class="avatar">
          <img :src="userStore.avatar" class="user-avatar" />
        </div>
        <div class="user-profile-text">
          <div class="user-name">{{ userStore.name }}</div>
          <div class="user-dept">{{ state.user?.dept?.deptName || '' }}<template
              v-if="state.user?.dept?.deptName && state.postGroup"> / </template>{{ state.postGroup || '' }}</div>
        </div>
        <el-dropdown @command="handleCommand" class="user-profile-dropdown" trigger="click">
          <span class="user-profile-arrow">
            <el-icon><ArrowRight /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <router-link to="/user/profile" @click="closeSidebarIfMobile">
                <el-dropdown-item>{{ $t('global.personal') }}</el-dropdown-item>
              </router-link>
              <el-dropdown-item divided command="logout">
                <span>{{ $t('global.logout') }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
      <!-- 明暗切替行：スマホでは旧 mobile-header の Analytics / powered by を同列に表示 -->
      <div class="sidebar-toolbar" :class="{ 'is-mobile-brand': appStore.device === 'mobile' }">
        <div v-if="appStore.device === 'mobile'" class="sidebar-mobile-brand">
          <div class="sidebar-mobile-brand__powered">
            <span>powered by</span>
            <div class="sidebar-mobile-brand__logos">
              <img
                v-if="isDarkMode"
                src="@/assets/images/eigaland_logo_name_banner_white.png"
                alt="EIGALAND"
              >
              <img
                v-else
                src="@/assets/images/eigaland_logo_name_banner_black.png"
                alt="EIGALAND"
              >
              <span class="sidebar-mobile-brand__sep" />
              <img src="@/assets/images/pia-eiga-logo.svg" alt="ぴあ映画">
            </div>
          </div>
        </div>
        <div class="sidebar-toolbar-actions">
          <button type="button" class="sidebar-toolbar-btn" :class="sideTheme"
            :title="isDarkMode ? $t('global.switchToLightMode') : $t('global.switchToDarkMode')"
            @click="toggleThemeMode">
            <i :class="isDarkMode ? 'fas fa-sun' : 'fas fa-moon'"></i>
          </button>
          <button
            v-if="appStore.device === 'mobile'"
            type="button"
            class="sidebar-toolbar-btn"
            :class="sideTheme"
            title="メニュー"
            @click="toggleMobileSidebar"
          >
            <i class="fas fa-bars"></i>
          </button>
          <button v-if="appStore.device !== 'mobile'" type="button" class="sidebar-toolbar-btn" :class="sideTheme"
            :title="$t('global.hideSidebar')" @click="hideSidebar">
            <i class="fas fa-angles-left"></i>
          </button>
        </div>
      </div>
      <div class="powered-by" v-if="!isCollapse && appStore.device === 'desktop'">
        <div class="powered-by-text">powered by</div>
        <div class="powered-by-logo">
          <img v-if="isDarkMode" class="powered-by-logo-eigaland" src="@/assets/images/eigaland_logo_name_banner_white.png" alt="EIGALAND">
          <img v-else class="powered-by-logo-eigaland" src="@/assets/images/eigaland_logo_name_banner_black.png" alt="EIGALAND">
          <span class="powered-by-separator"></span>
          <img src="@/assets/images/pia-eiga-logo.svg" alt="ぴあ映画">
        </div>
      </div>
    </div>
    <div id="sidebar-resizer" class="sidebar-resizer"></div>
  </div>
</template>

<script setup>
import { onMounted, watch, nextTick, computed, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { ElMessageBox } from 'element-plus';
import { ArrowRight } from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';
import Logo from './Logo'
import SidebarItem from './SidebarItem'
import variables from '@/assets/styles/variables.module.scss'
import useAppStore from '@/store/modules/app'
import useUserStore from '@/store/modules/user'
import useSettingsStore from '@/store/modules/settings'
import defaultSettings from '@/settings'
import usePermissionStore from '@/store/modules/permission'
import { getUserProfile } from "@/api/system/user";
import { runThemeViewTransition } from '@/utils/theme-transition'

const { t } = useI18n();
const emits = defineEmits(['setLayout']);

const route = useRoute();
const appStore = useAppStore()
const userStore = useUserStore()
const settingsStore = useSettingsStore()
const permissionStore = usePermissionStore()

const sidebarRouters = computed(() => permissionStore.sidebarRouters);
const showLogo = computed(() => settingsStore.sidebarLogo);
const sideTheme = computed(() => settingsStore.sideTheme);
const theme = computed(() => settingsStore.theme);
const isCollapse = computed(() => !appStore.sidebar.opened);
const isDarkMode = computed(() => settingsStore.themeMode === 'dark');
// 与 CSS 变量 --bg-sidebar 保持一致，避免深色模式仍落到旧的蓝灰菜单底色
const sidebarMenuBg = computed(() =>
  sideTheme.value === 'theme-dark' ? variables.menuBackground : variables.menuLightBackground
);

// 切换明暗模式（maoyan-pro と同じ斜めワイプ View Transition）
function toggleThemeMode() {
  runThemeViewTransition(() => {
    settingsStore.toggleThemeMode()
  })
}

// スマホ：ツールバーのメニューアイコンでサイドバー開閉
function toggleMobileSidebar() {
  if (appStore.sidebar.hide) return
  appStore.toggleSideBar(false)
}

// 手动隐藏侧边栏（可通过 layout 中的悬浮按钮重新显示）
function hideSidebar() {
  appStore.toggleSideBarHide(true);
}

const state = reactive({
  user: {},
  postGroup: {}
});

const activeMenu = computed(() => {
  const { meta, path } = route;
  // if set path, the sidebar will highlight the path you set
  if (meta.activeMenu) {
    return meta.activeMenu;
  }
  return path;
})

// 处理菜单选择事件 - 在移动端点击菜单项后关闭 sidebar
function handleMenuSelect() {
  // 检查是否为移动端
  if (appStore.device === 'mobile') {
    // 延迟关闭，确保路由跳转完成
    setTimeout(() => {
      appStore.closeSideBar({ withoutAnimation: false });
    }, 100);
  }
}

// 移动端点击下拉项后收起侧边栏
function closeSidebarIfMobile() {
  if (appStore.device === 'mobile') {
    setTimeout(() => {
      appStore.closeSideBar({ withoutAnimation: false });
    }, 100);
  }
}

// 用户下拉菜单命令（与 Navbar 一致）
function handleCommand(command) {
  switch (command) {
    case 'setLayout':
      closeSidebarIfMobile();
      emits('setLayout');
      break;
    case 'logout':
      logout();
      break;
    default:
      break;
  }
}

function logout() {
  ElMessageBox.confirm(t('global.confirmLogout'), t('global.tips'), {
    confirmButtonText: t('global.confirm'),
    cancelButtonText: t('global.cancel'),
    type: 'warning',
    closeOnClickModal: false,
  }).then(() => {
    userStore.logOut().then(() => {
      location.href = defaultSettings.homePath;
    });
  }).catch(() => {});
}

onMounted(() => {
  // getPost
  getUserProfile().then(res => {
    state.user = res.data;
    state.postGroup = res.postGroup;
  });
  // Resizable sidebar
  const sidebar = document.querySelector('.sidebar-container');
  const main = document.querySelector('.main-container');
  const resizer = document.getElementById('sidebar-resizer');
  console.log(sidebar, main, resizer);
  if (!sidebar || !main || !resizer) return;

  let isResizing = false;
  const MIN_WIDTH = 220;
  const MAX_WIDTH = 420;
  const EXPAND_THRESHOLD = 100; // 收起状态下，宽度超过此值自动展开
  const STORAGE_KEY = 'sidebar-width'; // localStorage 存储键名
  const DEFAULT_WIDTH = 340;

  // 统一宽度处理：转数字、四舍五入、再 clamp 到合法范围
  const normalizeWidth = (width) => {
    const n = Math.round(Number(width));
    const safe = Number.isFinite(n) ? n : DEFAULT_WIDTH;
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, safe));
  };

  // 将侧边栏宽度同步到 CSS 变量（用于刷新/清除内联样式后仍能保持宽度）
  const setSidebarWidthVar = (width) => {
    const w = normalizeWidth(width);
    const app = document.getElementById('app');
    if (app) app.style.setProperty('--sidebar-width', `${w}px`);
    return w;
  };

  // 从 localStorage 读取保存的宽度，如果没有则使用默认值 340
  let savedWidth = normalizeWidth(localStorage.getItem(STORAGE_KEY) || DEFAULT_WIDTH);
  // 初始化时先把宽度写入 CSS 变量，避免刷新后回落到 hardcode 的默认宽度
  setSidebarWidthVar(savedWidth);

  // 保存宽度到 localStorage（存储规范化后的值，避免小数/越界导致刷新不一致）
  const saveWidthToStorage = (width) => {
    const w = normalizeWidth(width);
    localStorage.setItem(STORAGE_KEY, String(w));
    return w;
  };

  const applyWidth = (width) => {
    const w = normalizeWidth(width);
    setSidebarWidthVar(w);
    sidebar.style.width = w + 'px';
    main.style.marginLeft = w + 'px';
    main.style.width = `calc(100% - ${w}px)`;
  }

  const clearInlineStyles = () => {
    sidebar.style.width = '';
    main.style.marginLeft = '';
    main.style.width = '';
  }

  const syncInitialWidth = () => {
    // ブレークポイントは device 判定（992px 基準）に統一。スマホUI では内联样式をクリアして CSS に委ねる
    if (appStore.device !== 'desktop') {
      clearInlineStyles();
      return;
    }
    // 如果侧边栏收起，清除内联样式让 CSS 控制
    if (isCollapse.value) {
      clearInlineStyles();
      return;
    }
    // 侧边栏展开时，应用保存的宽度
    applyWidth(savedWidth);
  }

  // 监听侧边栏收起/展开状态
  watch(isCollapse, (collapsed) => {
    nextTick(() => {
      if (collapsed) {
        // 收起时，保存当前宽度并清除内联样式
        const currentWidth = sidebar.getBoundingClientRect().width;
        if (currentWidth > 54) { // 只有当宽度大于收起时的宽度时才保存
          savedWidth = saveWidthToStorage(currentWidth); // 保存到 localStorage（规范化）
        }
        clearInlineStyles();
      } else {
        // 展开时，恢复保存的宽度
        if (appStore.device === 'desktop') {
          // 从 localStorage 重新读取宽度，确保使用最新值
          const storedWidth = localStorage.getItem(STORAGE_KEY) || DEFAULT_WIDTH;
          savedWidth = normalizeWidth(storedWidth);
          applyWidth(savedWidth);
        }
      }
    });
  });

  // 移动端 UI から桌面端 UI（临界宽度）に戻ったときは、上次的宽度ではなく最小宽度で显示する
  watch(() => appStore.device, (device, prevDevice) => {
    if (device === 'desktop' && prevDevice === 'mobile') {
      nextTick(() => {
        if (appStore.device !== 'desktop') return;
        // localStorage にも最小宽度を书き込む：这样 isCollapse 监听/ syncInitialWidth など
        // localStorage を再読み込みする経路もすべて最小宽度へ収束し、旧的宽的宽度で上书きされない
        savedWidth = saveWidthToStorage(MIN_WIDTH);
        applyWidth(MIN_WIDTH);
      });
    }
  });

  resizer.addEventListener('mousedown', (e) => {
    if (appStore.device !== 'desktop') return;
    isResizing = true;
    document.body.style.cursor = 'col-resize';
    e.preventDefault();
  })

  window.addEventListener('mousemove', (e) => {
    if (!isResizing) return;
    let newWidth = e.clientX;

    // 如果当前是收起状态，允许调整宽度
    if (isCollapse.value) {
      // 收起状态下，最小宽度为 54px（收起时的宽度），最大宽度不受限制（但不超过 MAX_WIDTH）
      newWidth = Math.max(54, Math.min(MAX_WIDTH, newWidth));
      applyWidth(newWidth);

      // 如果宽度超过展开阈值，自动展开侧边栏
      if (newWidth >= EXPAND_THRESHOLD) {
        savedWidth = saveWidthToStorage(newWidth); // 保存到 localStorage（规范化）
        appStore.toggleSideBar(false); // 展开侧边栏
      }
    } else {
      // 展开状态下，正常调整宽度
      newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      savedWidth = newWidth; // 保存调整后的宽度
      applyWidth(newWidth);
    }
  })

  window.addEventListener('mouseup', () => {
    if (!isResizing) return;

    // 如果侧边栏是收起状态，且最终宽度小于展开阈值，恢复到收起状态
    if (isCollapse.value) {
      const finalWidth = sidebar.getBoundingClientRect().width;
      if (finalWidth < EXPAND_THRESHOLD) {
        clearInlineStyles(); // 清除内联样式，让 CSS 控制为 54px
      } else {
        // 如果最终宽度大于等于展开阈值，保存宽度
        savedWidth = saveWidthToStorage(finalWidth);
      }
    } else {
      // 展开状态下，保存最终宽度到 localStorage
      const finalWidth = sidebar.getBoundingClientRect().width;
      savedWidth = saveWidthToStorage(finalWidth);
    }

    isResizing = false;
    document.body.style.cursor = '';
  })

  window.addEventListener('resize', syncInitialWidth);
  window.addEventListener('load', syncInitialWidth);
  syncInitialWidth();
});

</script>
<style lang="scss">
// 侧边栏容器背景统一走 --bg-sidebar（浅色白 / 深色 #1b1e24）
.sidebar-inner {
  background-color: var(--bg-sidebar);
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

// 底部固定区：不参与菜单区压缩，保证主题/隐藏按钮在窗口变矮时仍可见
.sidebar-footer {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;

  // iPhone 底部 home indicator 不遮挡主题/收起按钮：留出 safe-area 底部留白
  @media (max-width: 992px) {
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  &.is-collapsed {
    .sidebar-toolbar {
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 6px;
      padding: 8px 0;
    }
  }
}

// 工具栏：收起/展开均显示；隐藏侧边栏按钮仅桌面端
.sidebar-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 4px;
  padding: 6px 12px;
  border-top: 1px solid var(--border-color);
  flex-shrink: 0;
  min-width: 0;

  .sidebar-toolbar-actions {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    gap: 4px;
  }

  .sidebar-toolbar-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 0.85rem;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
      color: var(--text-primary);
    }

    &.theme-dark {
      color: #bfcbd9;

      &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: #f4f4f5;
      }
    }
  }

  /* スマホ：旧 mobile-header のブランドを明暗切替と同じ行へ */
  &.is-mobile-brand {
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
  }
}

.sidebar-mobile-brand {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;

  &__powered {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    color: var(--text-secondary);
    font-size: 0.62rem;
    font-weight: 500;
  }

  &__logos {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    img {
      height: 11px;
      width: auto;
    }
  }

  &__sep {
    width: 1px;
    height: 10px;
    background: var(--border-color);
  }
}

@media (max-width: 992px) {
  .sidebar-toolbar {
    .sidebar-toolbar-btn {
      width: 36px;
      height: 36px;
      font-size: 1rem;
    }
  }
}

.menu-item li {
  &.is-active {
    background: color-mix(in srgb, var(--brand-color) 20%, transparent);
    color: #000;
    border-left-color: var(--brand-color, #f9da49);
    font-weight: 700;

    &:hover {
      background: color-mix(in srgb, var(--brand-color) 20%, transparent) !important;
    }

    span.menu-title {
      color: #000;
      font-weight: 700;
    }
  }

  span.menu-title {
    font-size: 0.95rem;
    font-weight: 500;
    color: #4a4a4a;
  }
}

// 深色侧边栏配色下，菜单文字需要用浅色，避免与暗色背景对比度过低
.theme-dark .menu-item li {
  span.menu-title {
    color: #bfcbd9;
  }

  &.is-active span.menu-title {
    color: #fff;
  }
}

// user-profile
.user-profile {
  padding: 12px 20px;
  border-top: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0; // 固定在底部，不压缩
  
  // 移动端：减少 padding
  @media (max-width: 768px) {
    padding: 10px 16px;
    gap: 10px;
  }

  .user-profile-text {
    flex: 1;
    min-width: 0;
    font-size: 0.9rem;
  }

  .user-name {
    color: var(--text-primary);
    font-weight: 600;
  }

  .user-dept {
    color: var(--text-secondary);
    font-size: 0.75rem;
  }

  .user-profile-dropdown {
    flex-shrink: 0;
  }

  .user-profile-arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    cursor: pointer;
    color: var(--text-secondary);
    border-radius: 4px;
    transition: color 0.2s, background 0.2s;

    &:hover {
      color: var(--text-primary);
      background: rgba(0, 0, 0, 0.05);
    }

    .el-icon {
      font-size: 1rem;
    }
  }

  .avatar {
    width: 36px;
    height: 36px;
    background: var(--brand-color);
    color: var(--text-primary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.8rem;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
    }
  }
}

// powered-by
.powered-by {
  padding: 12px 20px;
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: color-mix(in srgb, var(--brand-color) 5%, transparent);
  flex-shrink: 0; // 固定在底部，不压缩
  min-width: 0;
  overflow: hidden;

  // 窗口高度较矮时优先保证上方工具栏可见
  @media (max-height: 520px) {
    display: none;
  }

  .powered-by-text {
    font-size: 0.7rem;
    color: var(--text-secondary);
    font-weight: 500;
    letter-spacing: 0.5px;
  }

  .powered-by-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-weight: 700;
    font-size: 0.85rem;
    color: var(--text-primary);
    max-width: 100%;
    min-width: 0;

    img {
      height: 18px;
      width: auto;
      max-width: 45%;
      object-fit: contain;
    }

    // EIGALAND logo 相对 pia 映画 logo 视觉偏小，单独放大以保持视觉平衡
    .powered-by-logo-eigaland {
      height: 26px;
    }

    .powered-by-separator {
      margin: 0 4px;
      color: var(--text-secondary);
      flex-shrink: 0;
    }
  }
}

.sidebar-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
}
</style>
